import { useEffect,useState } from "react";
import { Modal,Button } from "src/Components";
import { CoveredMatrixExecutionMethod,CreateCoveredMatrixOrderRequest } from "src/Interfaces/coveredMatrixOrder";
import { getCoveredMatrixLiveCriteria } from "src/Requests/coveredMatrixOrder";
import { getStepOrderBook } from "src/Requests/stepRequests";
import TimelineIcon from "@mui/icons-material/Timeline";
import ChartSearchParamUI from "src/Pages/CoveredCall/ChartSearchParamUI";
import { defaultCriterionType,isMaximumCostCriterion,isFixedCallPriceCriterion,tableRefreshInterval,criterionName,formatCriterionValue } from "../model";
import { CriterionValueInput } from "../CriterionValueInput";

export const CriteriaModal=({ draft,setDraft,optionName,onBack,onSubmit }: {
  draft: CreateCoveredMatrixOrderRequest;
  setDraft: (value: CreateCoveredMatrixOrderRequest) => void;
  optionName: (value: string) => string;
  onBack: () => void;
  onSubmit: () => void;
}) => {
  const [liveBasePrice,setLiveBasePrice]=useState<number|null>(null);
  const [liveCriteria,setLiveCriteria]=useState<Record<string,number|null>>({});
  const isFutureBasePurchase=draft.executionMethod===CoveredMatrixExecutionMethod.FutureBasePurchase;
  const selectedCriterionType=draft.items[0]?.criterionType??defaultCriterionType;
  const isMaximumCost=isMaximumCostCriterion(selectedCriterionType);
  const isFixedCallPrice=isFixedCallPriceCriterion(selectedCriterionType);
  const liveCriteriaKey=JSON.stringify(draft.items.map(({ optionIsin,criterionType }) => ({ optionIsin,criterionType })));
  useEffect(() => {
    if(isFutureBasePurchase||!draft.baseIsin) {
      setLiveBasePrice(null);
      return;
    }
    let disposed=false;
    setLiveBasePrice(null);
    getStepOrderBook(draft.baseIsin)
      .then((book) => {
        if(!disposed)
          setLiveBasePrice(book?.instrumentStatistic?.trade?.price??null);
      })
      .catch(() => {
        if(!disposed)
          setLiveBasePrice(null);
      });
    return () => { disposed=true; };
  },[draft.baseIsin,isFutureBasePurchase]);
  useEffect(() => {
    const items: Array<{ optionIsin: string; criterionType: number }>=JSON.parse(liveCriteriaKey);
    if(!draft.baseIsin||items.length===0) {
      setLiveCriteria({});
      return;
    }
    let disposed=false;
    const load=() => getCoveredMatrixLiveCriteria({
      baseIsin: draft.baseIsin,
      executionMethod: draft.executionMethod,
      manualBaseReferencePrice: draft.manualBaseReferencePrice,
      items,
    })
      .then((items) => {
        if(disposed)
          return;
        setLiveCriteria(Object.fromEntries(items.map((item) => [item.optionIsin,item.value])));
      })
      .catch(() => {
        if(!disposed)
          setLiveCriteria({});
      });
    load();
    const intervalId=window.setInterval(load,tableRefreshInterval);
    return () => {
      disposed=true;
      window.clearInterval(intervalId);
    };
  },[draft.baseIsin,draft.executionMethod,draft.manualBaseReferencePrice,liveCriteriaKey]);
  const setValue=(optionIsin: string,value: number|null) => setDraft({ ...draft,items: draft.items.map((item) => item.optionIsin===optionIsin? { ...item,criterionValue: value,criterionType: selectedCriterionType }:item) });
  const basePrice=isFutureBasePurchase? draft.manualBaseReferencePrice:liveBasePrice;
  const basePriceLabel=isFutureBasePurchase? "قیمت مبنای نماد پایه (قیمت دستی)":"قیمت لایو سرخط روبرو (نماد پایه)";
  return <Modal
    title="ماتریس معیار"
    onClose={onBack}
    modalBoxClassName="!w-[650px] !max-w-[95vw]"
    UI={<div className="p-4 flex flex-col gap-4" dir="rtl">
      <div>
        <div className="font-bold">ماتریس معیار</div>
        <div className="text-xs text-gray-500 mt-1">
          {isFixedCallPrice? "حداقل قیمت کال را برای هر آپشن وارد کنید؛ شرط با قیمت سرخط خرید کال بررسی می‌شود.":isMaximumCost? "بهای تمام‌شده لحظه‌ای هر آپشن به‌عنوان مقدار پیش‌فرض قرار می‌گیرد و قابل ویرایش است.":"مقدار حداقل % بازده روزانه را برای هر آپشن وارد کنید."}
        </div>
      </div>
      {!isFixedCallPrice? <div
        className="flex w-[610px] max-w-full items-center justify-between rounded bg-sky-50 px-3 py-2 text-xs"
      >
        <span className="font-semibold">{basePriceLabel}</span>
        <span className="text-base font-bold text-sky-900">
          {basePrice===null||basePrice===undefined? "-":Number(basePrice).toLocaleString()}
        </span>
      </div>:null}
      <div className="w-[610px] max-w-full max-h-[50vh] overflow-auto">
        <table className="w-full table-fixed text-xs">
          <thead className="bg-gray-50">
            <tr>
              <th className="w-8 p-2 text-center">#</th>
              <th className="w-36 p-2 text-right">آپشن</th>
              {!isFixedCallPrice? <th className="p-2 text-center w-14">نمودار</th>:null}
              <th className="w-28 p-2 text-center">مقدار لحظه‌ای</th>
              <th className="w-40 p-2 text-right">{criterionName(selectedCriterionType)}</th>
            </tr>
          </thead>
          <tbody>
            {draft.items.map((item,index) => <tr key={item.optionIsin} className="border-t">
              <td className="p-2 text-center text-gray-500">{index+1}</td>
              <td className="w-36 truncate p-2">{optionName(item.optionIsin)}</td>
              {!isFixedCallPrice? <td className="p-2 text-center">
                <ChartSearchParamUI
                  cellType="Price"
                  headerName={isMaximumCost? "LivePairPosition":"GainAfterCommissionDaily"}
                  row={{ key: `${draft.baseIsin}-${draft.baseIsin}-${item.optionIsin}` }}
                  trigger={<button
                    type="button"
                    title={isMaximumCost? "نمودار بهای تمام‌شده":"نمودار هیبریدی بازده روزانه"}
                    className="rounded p-1 text-sky-700 hover:bg-sky-50"
                  >
                    <TimelineIcon fontSize="small" />
                  </button>}
                />
              </td>:null}
              <td className="p-2 text-center font-medium" dir="ltr">
                {formatCriterionValue(liveCriteria[item.optionIsin],item.criterionType)}
              </td>
              <td className="w-40 p-2">
                <CriterionValueInput
                  name={item.optionIsin}
                  value={item.criterionValue}
                  allowNegative={!isFixedCallPriceCriterion(item.criterionType)}
                  onChange={(value) => setValue(item.optionIsin,value)}
                />
              </td>
            </tr>)}
          </tbody>
        </table>
      </div>
      <div className="flex justify-between">
        <Button variant="outlined" text="بازگشت" onClick={onBack} className="!w-20" />
        <Button variant="contained" text="ثبت" onClick={onSubmit} className="!w-20" />
      </div>
    </div>}
  />;
};
